import { useCallback, useEffect, useRef } from "react"
import * as jose from 'jose'
import { setToken } from "../../application/states/auth/auth-slice"
import { useAppDispatch, useAppSelector } from "../../application/states/hooks"
import { useAxiosInstances } from "../../infra/axios-instance"
import { useLocalStorageService } from "../local-storage"
import { useEndSession } from "./useEndSession"

// A cada 3 min confirma no backend que a sessão continua válida (usuário ativo, sessão não revogada).
// Se o backend responder 401, o interceptor do axios encerra a sessão.
const SESSION_CHECK_INTERVAL_MS = 3 * 60 * 1000
// Renova o token (1h) quando faltar menos que isso para expirar
const REFRESH_BEFORE_EXPIRY_SEC = 15 * 60
// Sem mouse/teclado por esse tempo (2h), a sessão é encerrada
const IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000
const IDLE_CHECK_INTERVAL_MS = 30 * 1000
// Grava a última atividade no máximo a cada 30s (compartilhada entre abas pelo localStorage)
const ACTIVITY_SAVE_THROTTLE_MS = 30 * 1000
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel']

export const useSessionKeepAlive = () => {
  const dispatch = useAppDispatch()
  const token = useAppSelector((state) => state.auth.token)
  const { axiosAuthInstance } = useAxiosInstances()
  const { setItem, getItem } = useLocalStorageService()
  const { endSession } = useEndSession()

  // O intervalo sempre usa a instância/token mais recentes
  const instanceRef = useRef(axiosAuthInstance)
  instanceRef.current = axiosAuthInstance
  const tokenRef = useRef(token)
  tokenRef.current = token

  const getLastActivity = useCallback(() => {
    return Number(getItem({ key: 'LAST_ACTIVITY' })) || Date.now()
  }, [getItem])

  const isIdle = useCallback(() => Date.now() - getLastActivity() >= IDLE_TIMEOUT_MS, [getLastActivity])

  const checkSession = useCallback(async () => {
    const currentToken = tokenRef.current
    if (!currentToken || isIdle()) return

    try {
      await instanceRef.current.get('/routes/authentication/session')

      const { exp } = jose.decodeJwt(currentToken)
      if (exp && exp - Date.now() / 1000 < REFRESH_BEFORE_EXPIRY_SEC) {
        const response = await instanceRef.current.post('/routes/authentication/refresh-token')
        const newToken: string | undefined = response.data?.result?.token
        if (newToken) {
          dispatch(setToken({ token: newToken }))
          setItem({ key: 'ACCESS_TOKEN', value: newToken })
        }
      }
    } catch {
      // 401 é tratado pelo interceptor do axios (logout); erro de rede: tenta de novo no próximo ciclo
    }
  }, [dispatch, setItem, isIdle])

  // Logout por inatividade
  useEffect(() => {
    if (!token) return

    let lastSaved = 0
    const registerActivity = () => {
      const now = Date.now()
      if (now - lastSaved < ACTIVITY_SAVE_THROTTLE_MS) return
      lastSaved = now
      setItem({ key: 'LAST_ACTIVITY', value: String(now) })
    }

    registerActivity()
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, registerActivity, { passive: true }))

    const interval = setInterval(() => {
      if (isIdle()) {
        endSession('Sessão encerrada por inatividade. Faça login novamente.')
      }
    }, IDLE_CHECK_INTERVAL_MS)

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, registerActivity))
      clearInterval(interval)
    }
  }, [token, setItem, isIdle, endSession])

  useEffect(() => {
    if (!token) return
    checkSession()
    const interval = setInterval(checkSession, SESSION_CHECK_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [token, checkSession])
}
